import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import Task from '@src/slide/infrastructure/module/dependent/taskManager/task';
import type { IBaseModel } from '../Interface/base/base-model.interface';
import TaskGroup from '@src/slide/infrastructure/module/dependent/taskManager/taskGroup';
import ModelConverterBridge from '@src/slide/domain/ui-model/element-modeles/convert/model-converter-bridge';

export default class ConvertEngine {
    /**
     * 通用方法，转换数组类型 ui-model
     *
     * ⚠️注意：首个参数需要传入待转换数组 model 的构造方法。原因在于 ConvertEngine 类被各个类所依赖，
     * 而此类不应当反向依赖类的实现以避免循环依赖
     * @param getChildModelConstructor
     * @param sdkArray
     * @param uiArray
     * @param parentModel
     * @param taskGroupPriority
     * @param taskGroupParam
     */
    public static async convertArrayDataSdkToUi(
        getChildModelConstructor: (type: string) => (new () => (IBaseModel | undefined)) | undefined,
        sdkArray: Array<any>,
        uiArray: Array<any>,
        parentModel: any = null,
        taskGroupPriority?: number,
        taskGroupParam?: TaskGroup,
    ): Promise<void> {
        if (!sdkArray || !uiArray) {
            return;
        }

        const elements: Array<any> = [];
        const reuseModelArray = uiArray.slice(0, uiArray.length);

        const taskgroup = taskGroupParam ? taskGroupParam : parentModel?.sharedConvertTaskgroup;
        if (taskGroupPriority !== undefined) {
            taskgroup.priority = taskGroupPriority;
        }
        const taskPromises: any = [];
        // eslint-disable-next-line complexity
        sdkArray.forEach((childSdkData, childSdkIndex) => {
            if (!childSdkData) {
                return;
            }
            let uiModel: IBaseModel | null = ConvertEngine.getReuseModel(reuseModelArray, childSdkData);
            if (!uiModel) {
                const uiType = ModelConverterBridge.getUiTypeBySdkType(childSdkData.type);
                const ChildModelConstructor = getChildModelConstructor(uiType);

                const taskId = `convert_${parentModel.id}_${childSdkData.id}`;
                const executingTask = taskgroup.getTask(taskId);
                if (executingTask) {
                    // 如果当前已经有已经在异步转换过程中的了，则不再重复创建，避免覆盖掉前面可能已经绑定其他事件的uiModel
                    const task = {
                        promise: new Promise<void>((resolve) => {
                            executingTask.promise.then((boundUiModel) => {
                                elements[childSdkIndex] = boundUiModel;
                                resolve();
                            });
                        }),
                    };
                    taskPromises.push(task.promise);
                } else {
                    uiModel = (ChildModelConstructor ? new ChildModelConstructor() : null) ?? null;
                    if (!uiModel) {
                        return;
                    }
                    uiModel.parent = parentModel;
                    uiModel.selectionService = parentModel?.selectionService;
                    uiModel.sdkData = childSdkData;

                    if (parentModel?.asyncConvertChildren) {
                        const task = new Task(taskId, TaskPriority.NORMAL, async () => {
                            await uiModel?.bindSDK(childSdkData);
                            return uiModel;
                        });
                        taskgroup?.addTask(task);
                        taskPromises.push(task.promise);
                    } else {
                        uiModel.bindSDK(childSdkData);
                    }
                }
            }
            // masterId 会跟随 list 的变化而变化（例如拖拽版式到不同的母版里），因此额外更新 masterId
            if ((uiModel as any)?.masterId && childSdkData.masterId) {
                (uiModel as any).masterId = childSdkData.masterId;
            }
            elements.push(uiModel);
        });

        reuseModelArray.forEach((removedUiModel) => {
            removedUiModel.unbindSDK();
        });

        if (taskgroup.getTaskCount()) {
            TaskManager.addTask(taskgroup);
        }
        if (taskPromises.length) {
            // 等待所有的子元素convert完成
            await Promise.all(taskPromises);
        }

        uiArray.splice(0, uiArray.length, ...elements);
    }

    public static getReuseModel(models: Array<any>, sdkData: any) {
        let model = null;
        for (let index = 0; index < models.length; index++) {
            const uiModel = models[index];
            if (uiModel.sdkData === sdkData || uiModel.sdkDataRef === sdkData) {
                model = models[index];
                models.splice(index, 1);
                break;
            }
        }
        return model;
    }
}

